import {
  ApolloClient,
  InMemoryCache,
  type NormalizedCacheObject,
} from '@apollo/client';
import hirerGraphClient from '@seek/hirer-graphql-client';
import type { RequestMetadata } from '@seek/hirer-graphql-client/lib-types/types';
import type { GraphQLFormattedError } from 'graphql';
import type { AppMode } from '../context/PricingContextProvider';
import { getLogger } from './logger/logger';
import type { EmployerSiteNames } from '@seek/hirer-graphql-client/lib-types/constants/headers';
import { isLocal } from './Environment';
import { createAuthService } from '../services/authService/authService';

type GraphClientProps = {
  appMode: AppMode;
  site: EmployerSiteNames;
  environment: string;
};

const getXSeekSiteHeader = (appMode: AppMode): string => {
  switch (appMode) {
    case 'PREVIEW':
      return 'hirer-ad-price-lookup-preview';
    default:
      return 'hirer-ad-price-lookup';
  }
};

const getSourceHeaderValue = (appMode: AppMode): string => {
  switch (appMode) {
    case 'PREVIEW':
      return 'price-checker-preview';
    default:
      return 'price-checker';
  }
};

const getHeaders = (appMode: AppMode) => () => ({
  source: getSourceHeaderValue(appMode),
});

const logGraphError = (
  error: GraphQLFormattedError,
  operationName?: string,
  metadata?: RequestMetadata,
) => {
  getLogger().error(error.message, {
    activity: operationName,
    error,
    ...metadata,
    operationName,
  });
};

const logGenericError = (
  thrownErr: unknown,
  operationName?: string,
  metadata?: RequestMetadata,
) => {
  // casts unknown while https://github.com/SEEK-Jobs/hirer-graphql-client/issues/64 is resolved
  const error =
    thrownErr && typeof thrownErr === 'object'
      ? (thrownErr as Error)
      : new Error('Unknown error');

  getLogger().error(error.message, {
    activity: operationName,
    error,
    operationName,
    ...metadata,
  });
};

const createHirerGraphClient = ({
  appMode,
  site,
  environment,
}: GraphClientProps): ApolloClient<NormalizedCacheObject> => {
  const authService = createAuthService();
  const isLocalEnvironment = isLocal(environment);

  const getToken = isLocalEnvironment
    ? // @ts-ignore
      () => Promise.resolve(global.HIRER_GRAPHQL_API_AUTH_TOKEN ?? 'mockToken')
    : authService.getToken;

  return hirerGraphClient({
    site,
    appName: getXSeekSiteHeader(appMode),
    getAuthToken: getToken,
    renewAuthToken: getToken,
    login: authService.login,
    logMetrics: () => {},
    logGraphQLError: logGraphError,
    logError: logGenericError,
    getHeaders: getHeaders(appMode),
    cache: new InMemoryCache(),
    maxRetry: isLocalEnvironment ? 1 : 3,
    uri: isLocalEnvironment
      ? 'https://talent.staging.seek.com.au/graphql'
      : undefined,
  });
};

export { createHirerGraphClient };
